import { useEditor as useTiptapEditor } from "@tiptap/react";
import { useEffect, useImperativeHandle } from "react";
import type { EditorHookProps } from "../types/hook";
import { CoreEditorExtensions } from "../extentions/extention";
import { getEditorRefHelpers } from "../helpers/editor-ref";
import { CoreEditorProps } from "../props";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";

export const useEditor = (props: EditorHookProps) => {
  const {
    autofocus = false,
    autoExpandConfig,
    editable = true,
    editorProps = {},
    extensions = [],
    forwardedRef,
    handleEditorReady,
    initialValue,
    isTouchDevice,
    mentionHandler,
    onChange,
    onEditorFocus,
    onBlur,
    onTransaction,
    placeholder,
    tabIndex,
    editorClassName = "",
    value,
    id,
    uploadImage
  } = props;
  const editor = useTiptapEditor(
    {
      editable,
      immediatelyRender: false,
      shouldRerenderOnTransaction: false,
      autofocus,
      parseOptions: { preserveWhitespace: true },
      editorProps: {
        ...CoreEditorProps({
          editorClassName,
          autoExpandConfig
        }),
        ...editorProps
      },
      extensions: [
        ...CoreEditorExtensions({
          mentionHandler,
          isTouchDevice,
          editable,
          placeholder,
          tabIndex,
          uploadImage
        }),
        ...extensions
      ],
      content: initialValue,
      onCreate: () => handleEditorReady?.(true),
      onTransaction: () => {
        onTransaction?.();
      },
      onUpdate: ({ editor: instance }) =>
        onChange?.(instance.getJSON(), instance.getHTML()),
      onDestroy: () => handleEditorReady?.(false),
      onFocus: onEditorFocus,
      onBlur: onBlur
    },
    [editable]
  );

  useEffect(() => {
    if (value === null || value === undefined || !editor || editor.isDestroyed)
      return;
    if (!editor.isFocused)
      try {
        editor.commands.setContent(value, {
          parseOptions: { preserveWhitespace: true },
          emitUpdate: false
        });
      } catch (error) {
        const message = getErrorMessage(error);
        toast.error(message);
      }
  }, [editor, value, id]);

  useImperativeHandle(forwardedRef, () => getEditorRefHelpers({ editor }), [
    editor
  ]);

  return editor;
};
